import { useState, useEffect } from 'react';
import WizardLayout from './WizardLayout';
import { useWizard }  from '../../contexts/WizardContext';
import { useProfile } from '../../contexts/ProfileContext';

import { createMemoriesFromWizardStep, createWizardMemories } from '../../utils/wizardMemoryTemplates';

export default function StepChild({ navigate, embedded = false, draft: externalDraft, setDraft: setExternalDraft }) {
  // Use either external draft (for embedded mode) or context draft (for wizard mode)
  const wizardContext = useWizard();
  const { refresh } = useProfile();
  
  const draft = embedded ? externalDraft : wizardContext.draft;
  const setDraft = embedded ? setExternalDraft : wizardContext.setDraft;
  
  const [alias, setAlias] = useState(draft?.child?.alias || '');
  const [birthYear, setBirthYear] = useState(draft?.child?.birthYear || '');
  const [diagnoses, setDiagnoses] = useState(draft?.child?.diagnoses || []);
  const [diagnosisYear, setDiagnosisYear] = useState(draft?.child?.diagnosisYear || '');
  const [familyStatus, setFamilyStatus] = useState(draft?.child?.familyStatus || '');
  const [siblings, setSiblings] = useState(
    draft?.child?.siblings != null ? String(draft.child.siblings) : ''
  );
  const [siblingsError, setSiblingsError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setSavedMemories] = useState([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const SIBLINGS_MAX = 15;

  const parseSiblings = raw => {
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '') return { value: null, error: '' };
    if (!/^\d+$/.test(trimmed)) {
      return { value: null, error: 'Vennligst oppgi et helt tall fra 0 og oppover.' };
    }
    const n = Number(trimmed);
    if (n > SIBLINGS_MAX) {
      return { value: null, error: `Vennligst oppgi et tall fra 0 til ${SIBLINGS_MAX}.` };
    }
    return { value: n, error: '' };
  };
  
  // Keep local state in sync with draft changes (important for embedded mode)
  useEffect(() => {
    if (draft) {
      setAlias(draft.child?.alias || '');
      setBirthYear(draft.child?.birthYear || '');
      setDiagnoses(draft.child?.diagnoses || []);
      setDiagnosisYear(draft.child?.diagnosisYear || '');
      setFamilyStatus(draft.child?.familyStatus || '');
      setSiblings(draft.child?.siblings != null ? String(draft.child.siblings) : '');
    }
  }, [draft]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2005 + 1 }, (_, i) => 2005 + i);

  const toggleDx = dx =>
    setDiagnoses(diagnoses.includes(dx)
      ? diagnoses.filter(d => d !== dx)
      : [...diagnoses, dx]);

  // Handle changes to form fields
  const handleChange = () => {
    // Only propagate siblings if the current input parses cleanly; otherwise omit
    // so an in-progress invalid value doesn't poison the draft.
    const { value: siblingsValue, error: siblingsParseError } = parseSiblings(siblings);
    const updatedDraft = {
      ...draft,
      child: {
        alias,
        birthYear: birthYear ? Number(birthYear) : '',
        diagnoses,
        diagnosisYear: diagnosisYear ? Number(diagnosisYear) : '',
        familyStatus,
        siblings: siblingsParseError ? draft?.child?.siblings ?? null : siblingsValue
      }
    };

    // In embedded mode, just update the draft
    if (embedded) {
      setDraft(updatedDraft);
      return;
    }
  };

  // Update draft when form fields change
  useEffect(() => {
    if (embedded) {
      handleChange();
    }
  }, [alias, birthYear, diagnoses]); // eslint-disable-line react-hooks/exhaustive-deps

  const skipWizard = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // Create memories if user has provided any child information
      if (birthYear || alias || diagnoses.length > 0) {
        const stepData = {
          alias: alias || '',
          birthYear: birthYear ? Number(birthYear) : null,
          diagnoses: diagnoses || []
        };
        
        const memories = createMemoriesFromWizardStep('child', stepData);
        
        if (memories.length > 0) {
          const result = await createWizardMemories(memories);
          if (result.success) {
            setSavedMemories(memories);
            setShowSaveConfirmation(true);
            setTimeout(() => setShowSaveConfirmation(false), 3000);
          }
        }
      }

      // Update profile with wizardSkipped flag (comment out profile save for now)
      // TODO: Remove profile saving entirely once migration is complete
      /*
      await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...draft,
          wizardSkipped: true,
          child: { 
            alias: alias || '', 
            birthYear: birthYear ? Number(birthYear) : null, 
            diagnoses: diagnoses || [] 
          }
        })
      });
      */
      
      // Wait until shared context is up-to-date
      await refresh();
      
      // Guard now sees correct flags
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error skipping wizard:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onNext = async ({ skip } = {}) => {
    if (isSubmitting || embedded) return;
    setIsSubmitting(true);
    
    try {
      if (skip) {
        await skipWizard();
        return;
      } else {
        const { value: siblingsValue, error: siblingsParseError } = parseSiblings(siblings);
        if (siblingsParseError) {
          setSiblingsError(siblingsParseError);
          return;
        }
        setSiblingsError('');

        // Create memories from current step data
        const stepData = {
          alias: alias || '',
          birthYear: Number(birthYear),
          diagnoses: diagnoses || [],
          diagnosisYear: diagnosisYear ? Number(diagnosisYear) : null,
          familyStatus: familyStatus || '',
          siblings: siblingsValue
        };

        const memories = createMemoriesFromWizardStep('child', stepData);

        if (memories.length > 0) {
          const result = await createWizardMemories(memories);
          if (result.success) {
            setSavedMemories(memories);
            setShowSaveConfirmation(true);
            setTimeout(() => setShowSaveConfirmation(false), 3000);
          }
        }

        // Update local wizard state for flow continuity
        setDraft({
          ...draft,
          child: {
            alias,
            birthYear: Number(birthYear),
            diagnoses,
            diagnosisYear: diagnosisYear ? Number(diagnosisYear) : null,
            familyStatus,
            siblings: siblingsValue
          }
        });

        navigate('/onboarding/location');
      }
    } catch (error) {
      console.error('Error in onNext:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // The form content
  const formContent = (
    <>
      <h2 className="text-xl font-semibold mb-6">Om barnet</h2>
      

      {/* Save confirmation */}
      {showSaveConfirmation && (
        <div className="mb-4 p-3 bg-sage-50 border border-sage-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-sage-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sage-800 font-medium">Lagret: </span>
            <span className="text-sage-700">
              {alias && `${alias}, `}
              {birthYear && `${birthYear}, `}
              {diagnoses.length > 0 && diagnoses.join(', ')}
            </span>
          </div>
        </div>
      )}

      <label className="block mb-4">
        Fornavn (valgfritt)
        <input 
          value={alias} 
          onChange={e => setAlias(e.target.value)} 
          className="w-full p-2 border rounded mt-1" 
        />
      </label>

      <label className="block mb-4">
        Fødselsår *
        <select 
          value={birthYear} 
          onChange={e => setBirthYear(e.target.value)} 
          className="w-full p-2 border rounded mt-1"
        >
          <option value="">Velg år</option>
          {years.map(y => <option key={y}>{y}</option>)}
        </select>
      </label>

      <div className="mb-4">
        <div className="mb-1">Diagnoser *</div>
        <div className="flex flex-wrap gap-2">
          {['ADHD','Autisme','Tourettes syndrom'].map(dx => (
            <button
              key={dx}
              type="button"
              onClick={() => toggleDx(dx)}
              className={`px-3 py-1 rounded-full transition-colors ${diagnoses.includes(dx) ? 'bg-primary text-white' : 'bg-neutral-200 text-text-secondary'}`}
            >{dx}</button>
          ))}
        </div>
      </div>

      {/* Diagnosis timing - only show if diagnoses are selected */}
      {diagnoses.length > 0 && (
        <label className="block mb-4">
          Ca. når ble diagnosen stilt? (år)
          <select
            value={diagnosisYear}
            onChange={e => setDiagnosisYear(e.target.value)}
            className="w-full p-2 border rounded mt-1"
          >
            <option value="">Velg år (valgfritt)</option>
            {years.map(y => <option key={y}>{y}</option>)}
          </select>
          <p className="text-xs text-text-muted mt-1">
            Dette hjelper oss å forstå hvor mye erfaring dere har med støttesystemet
          </p>
        </label>
      )}

      <label className="block mb-4">
        Familiesituasjon
        <select 
          value={familyStatus} 
          onChange={e => setFamilyStatus(e.target.value)} 
          className="w-full p-2 border rounded mt-1"
        >
          <option value="">Velg (valgfritt)</option>
          <option value="sammen">Foreldre bor sammen</option>
          <option value="skilt">Foreldre er skilt/separert</option>
          <option value="enslig">Enslig forsørger</option>
          <option value="annet">Annet</option>
        </select>
      </label>

      <label className="block mb-4">
        Antall søsken (valgfritt)
        <input
          type="number"
          inputMode="numeric"
          min="0"
          max={SIBLINGS_MAX}
          step="1"
          value={siblings}
          onChange={e => {
            setSiblings(e.target.value);
            if (siblingsError) setSiblingsError('');
          }}
          onBlur={e => {
            const { error } = parseSiblings(e.target.value);
            setSiblingsError(error);
          }}
          className={`w-full p-2 border rounded mt-1 ${siblingsError ? 'border-red-500' : ''}`}
          placeholder="F.eks. 2"
        />
        {siblingsError && (
          <p className="text-sm text-red-600 mt-1">{siblingsError}</p>
        )}
      </label>

    </>
  );

  // For embedded mode, just return the form content
  if (embedded) {
    return (
      <div className="border-b pb-8">
        {formContent}
      </div>
    );
  }

  // For wizard mode, wrap in WizardLayout
  return (
    <WizardLayout step={1} total={5} nextDisabled={!birthYear} onNext={onNext}>
      {formContent}
    </WizardLayout>
  );
}
